// Notifications.jsx
import React, { useEffect, useState } from 'react';
import { IconButton, Badge, Tooltip } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { toast } from 'react-toastify';
import NotificationModal from './notificationModal';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../services/notificationContext';

const Notifications = () => {
  const { notifications, setNotifications, hasFetched, setHasFetched } = useNotifications();
  const [openModal, setOpenModal] = useState(false);
  const [isInitialFetch, setIsInitialFetch] = useState(true);
  const [prevNotificationCount, setPrevNotificationCount] = useState(0);
  const location = useLocation();
  const baseURL = process.env.REACT_APP_BASE_URL;

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        toast.warning('No authentication token found.');
        return;
      }

      const response = await fetch(`${baseURL}notifications`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();

        const uniqueNotifications = data.filter(
          (newNotification, index, self) =>
            index === self.findIndex((notif) => notif.notificationID === newNotification.notificationID)
        );

        if (uniqueNotifications.length !== prevNotificationCount) {
          if (isInitialFetch) {
            if (uniqueNotifications.length > 0) {
              // Leave this out for now
              //toast.info(`You have ${uniqueNotifications.length} total notification(s).`);
            }
            setIsInitialFetch(false);
          } else if (uniqueNotifications.length > prevNotificationCount) {
            const newCount = uniqueNotifications.length - prevNotificationCount;
            toast.info(`You have ${newCount} new notification(s). Click the icon for details.`);
          }
          setPrevNotificationCount(uniqueNotifications.length);
        }

        setNotifications(uniqueNotifications);
      } else {
        toast.error(`Failed to fetch notifications: ${response.statusText}`);
      }
    } catch (error) {
      toast.error(`Error fetching notifications: ${error.message}`);
    }
  };

  const handleClearNotification = async (id) => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        toast.warning('No authentication token found.');
        return;
      }

      const response = await fetch(`${baseURL}Notifications/${id}/clear`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Notification cleared successfully.');
        fetchNotifications();
      } else {
        toast.error(`Failed to clear notification: ${response.statusText}`);
      }
    } catch (error) {
      toast.error(`Error clearing notification: ${error.message}`);
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        toast.warning('No authentication token found.');
        return;
      }

      const response = await fetch(`${baseURL}Notifications/ClearAll`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('All notifications cleared successfully.');
        fetchNotifications();
      } else {
        toast.error(`Failed to clear all notifications: ${response.statusText}`);
      }
    } catch (error) {
      toast.error(`Error clearing all notifications: ${error.message}`);
    }
  };

  useEffect(() => {
    if (!hasFetched) {
      fetchNotifications();
      setHasFetched(true);
    }

    const interval = setInterval(() => {
      fetchNotifications();
    }, 15000); // 15 seconds polling interval

    return () => clearInterval(interval);
  }, [hasFetched, setHasFetched, notifications]);

  return (
    <>
      <Tooltip title="Notifications" placement="right">
        <IconButton color="primary" onClick={() => setOpenModal(true)} sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out, color 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.3)'
              },
            }}>
          <Badge badgeContent={notifications.length} color="error">
            <NotificationsIcon sx={{ fontSize: '2.5rem' }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <NotificationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        notifications={notifications}
        onClearNotification={handleClearNotification}
        onClearAllNotifications={handleClearAllNotifications}
      />
    </>
  );
};

export default Notifications;
